import React, { useState, useEffect } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import EmployeeDetailsModal from './EmployeeDetailsModal/index.js';
import ChangeRequestModal from './ChangeRequestModal.js';
import { parseUserDataForUpdate } from './EmployeeDetailsModal/parseEmployeeData';
import EffectiveDateModal from './EffectiveDateModal.js';

/**
 * 
 * @param {*} param0 
 * @returns 
 */
const EmployeeModalHandlerAdminSite = ({
  open,
  isEdit,
  close,
  currentEmployee,
  classData,
  divisions,
  currentAccountMeta,
  addUser,
  updateUser,
  createChangeRequest,
  deleteChangeRequest,
  submitChangeRequest,
  loader,
  id,
  dispatch,
  listFiles,
  createFolder,
  uploadFile,
  downloadFile,
  removeFile
}) => {
  const [changeRequestModalOpen, setChangeRequestModalOpen] = useState(false);
  const [employeeDetailsModalOpen, setEmployeeDetailsModalOpen] = useState(open);
  const [changeRequestData, setChangeRequestData] = useState(null);
  const [userUpdateData, setUserUpdateData] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [effectiveDateModalOpen, setEffectiveDateModalOpen] = useState(false);
  useEffect(() => {
    setEmployeeDetailsModalOpen(open);
  }, [open, currentEmployee]);

  /**
   * Handles submitting a change request, and then a user update if necessary.
   *
   * @param {string} changeRequestID - ID of the change request.
   * @param {Object} changeRequestUpdateData - Data for the change request update.
   */
  const _submitChangeRequest = async (changeRequestID, changeRequestUpdateData) => {
    setEffectiveDate(changeRequestUpdateData.effectiveDate);
    const changeRequestSuccess = await dispatch(submitChangeRequest(changeRequestID, {
      userID: currentEmployee.userID,
      ...changeRequestUpdateData
    }));
    if (changeRequestSuccess) {
      setChangeRequestModalOpen(false);
    }
    if (changeRequestSuccess && userUpdateData) {
      const dataForUpdate = parseUserDataForUpdate(userUpdateData, currentEmployee, divisions);
      await updateUser({
        ...dataForUpdate,
        effectiveDate: changeRequestUpdateData.effectiveDate
      }, currentEmployee.userID);
    }
    close();
  };

  /**
   * Handles user update with a change request, by setting the data to be updated after the change request in state, submitting the change request, and then using the effective date of the change 
   * request as the effective date of the user update.
   *
   * @param {Object} dataForUserUpdate - Data to update the user.
   * @param {Object} dataForChangeRequest - Data for the change request.
   * @returns {boolean} True if the update was successful, false otherwise.
   */
  const _handleUserUpdateWithChangeRequest = (dataForUserUpdate, dataForChangeRequest, changeRequestType) => {
    setChangeRequestData({
      type: changeRequestType,
      details: dataForChangeRequest
    });
    setUserUpdateData(dataForUserUpdate);
    setEmployeeDetailsModalOpen(false);
    setChangeRequestModalOpen(true);
    return true;
  };

  /**
   * Handles change request by setting the change request type and opening the modal to initiate the change request in the parent component
   * 
   * @param {Object} dataForChangeRequest - Data for the change request
   */
  const _handleChangeRequest = (dataForChangeRequest, changeRequestType) => {
    setChangeRequestData({
      type: changeRequestType,
      details: dataForChangeRequest
    });
    setChangeRequestModalOpen(true);
    setEmployeeDetailsModalOpen(false);
    return true;
  };

  /**
   * Updates user information conditionally, depending on which fields of the user data have changed
   * 
   * @param {Object} regularUpdateData - Regular update data
   * @param {Object} personalInfoChanges - Personal info changes
   * @param {Object} employmentDetailsChanges - Employment details changes
   * @returns {Promise<boolean>} True if the update was successful, false otherwise
   */
  const _updateUser = (regularUpdateData, personalInfoChanges, employmentDetailsChanges) => {
    let changeRequestType = 'Edit Member Details';
    if (regularUpdateData && personalInfoChanges) {
      return _handleUserUpdateWithChangeRequest(regularUpdateData, personalInfoChanges, changeRequestType);
    }
    if (regularUpdateData && employmentDetailsChanges) {
      changeRequestType = 'Edit Employment Details';
      return _handleUserUpdateWithChangeRequest(regularUpdateData, employmentDetailsChanges, changeRequestType);
    }
    if (personalInfoChanges) {
      return _handleChangeRequest(personalInfoChanges, changeRequestType);
    }
    if (employmentDetailsChanges) {
      changeRequestType = 'Edit Employment Details';
      return _handleChangeRequest(employmentDetailsChanges, changeRequestType);
    }
    if (regularUpdateData) {
      const dataForUpdate = parseUserDataForUpdate(regularUpdateData, currentEmployee, divisions);
      setUserUpdateData(dataForUpdate);
      setEmployeeDetailsModalOpen(false);
      setEffectiveDateModalOpen(true);
    }
    return false;
  };

  /**
   * Updates user data with the effective date.
   */
  const userUpdate = async () => {
    const userData = {
      ...userUpdateData,
      effectiveDate: dates.getUTC(effectiveDate)
    };
    const userUpdateSuccess = await updateUser(userData, currentEmployee.userID);
    if (userUpdateSuccess) {
      setEffectiveDateModalOpen(false);
    }
  };

  /**
   * 
   */
  const closeChangeRequestModal = () => {
    setChangeRequestModalOpen(false);
    close();
  };
  if (effectiveDateModalOpen) {
    return /*#__PURE__*/React.createElement(EffectiveDateModal, {
      open: true,
      onClick: userUpdate,
      handleInputChange: (field, value) => setEffectiveDate(value),
      onClose: () => {
        setEffectiveDateModalOpen(false);
        setEmployeeDetailsModalOpen(true);
      }
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmployeeDetailsModal, {
    open: employeeDetailsModalOpen,
    isEdit: isEdit,
    close: close,
    currentEmployee: currentEmployee,
    classData: classData,
    divisions: divisions,
    currentAccountMeta: currentAccountMeta,
    addUser: addUser,
    updateUser: _updateUser,
    isAdmin: true,
    loader: loader
  }), changeRequestModalOpen ? /*#__PURE__*/React.createElement(ChangeRequestModal, {
    open: changeRequestModalOpen,
    onClose: closeChangeRequestModal,
    createChangeRequest: createChangeRequest,
    deleteChangeRequest: deleteChangeRequest,
    submitChangeRequest: _submitChangeRequest,
    changeRequestData: changeRequestData,
    type: "users",
    id: id,
    dispatch: dispatch,
    listFiles: listFiles,
    createFolder: createFolder,
    uploadFile: uploadFile,
    downloadFile: downloadFile,
    removeFile: removeFile,
    loader: loader
  }) : null);
};
export default EmployeeModalHandlerAdminSite;